import { Component, Show, For, createMemo, createSignal } from "solid-js";
import type { ProposedAction } from "../../api/types";
import { Spinner } from "../ui/Spinner";
import { ProposalDiff } from "./ProposalDiff";

export interface ActionDiffCardProps {
  action: ProposedAction;
  selected: boolean;
  selectable: boolean;
  onToggle: () => void;
  /** When true, show compact layout (used inside InlineProposal) */
  compact?: boolean;
}

// Fields that are bare UUIDs with no display value — always skip.
// Friendly counterparts (status_name, assignee_name, parent_title,
// item_type_name) are NOT skipped and flow through as display values.
const SKIP_FIELDS = new Set([
  "work_item_id",
  "id",
  "expected_updated_at",
  "project_id",
  "status_id",
  "assignee_id",
  "parent_id",
  "item_type_id",
  "reporter_id",
  "conversation_id",
]);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(v: unknown): boolean {
  return typeof v === "string" && UUID_RE.test(v);
}

function renderFieldValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (key === "priority") return String(value);
  if (key === "estimate" && typeof value === "object" && value !== null) {
    const e = value as { value: number; unit: string };
    return `${e.value} ${e.unit.replace(/_/g, " ")}`;
  }
  if (key === "labels" && Array.isArray(value)) {
    return value.length === 0 ? "none" : value.join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const FIELD_LABELS: Record<string, string> = {
  display_id: "ID",
  parent_display_id: "Parent",
  parent_title: "Parent",
  status_name: "Status",
  assignee_name: "Assignee",
  item_type_name: "Type",
};

function humanKey(key: string): string {
  return (
    FIELD_LABELS[key] ??
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

interface DiffRow {
  key: string;
  label: string;
  before: string | null;
  after: string;
}

const ITEM_FIELD_ORDER = [
  "display_id",
  "title",
  "item_type_name",
  "parent_display_id",
  "parent_title",
  "priority",
  "description",
];

function buildDiffRows(action: ProposedAction): DiffRow[] {
  const opType = getOperationType(action.tool_name);
  const before = action.before_snapshot ?? null;

  // Deletes have no meaningful "after" — show the snapshot of what's being
  // deleted, sourced from before_snapshot, rendered as plain fields.
  const source: Record<string, unknown> =
    opType === "delete" && before
      ? (before as Record<string, unknown>)
      : action.user_modification || action.parameters;

  const usingBefore = opType === "delete" && before !== null;
  const isItemCreationLike = opType === "create" || opType === "delete";

  const hasParentDisplayId =
    typeof source.parent_display_id === "string" ||
    (before && typeof before.parent_display_id === "string");

  // Title is already in the group header for creates/deletes — but only drop it
  // from the rows when there are other identifying fields to show. Otherwise
  // (e.g. old delete proposals with no snapshot) keep it so the card isn't empty.
  const hasOtherIdentifyingFields = Object.entries(source).some(
    ([k, v]) =>
      !SKIP_FIELDS.has(k) && k !== "title" && !isUuid(v) && v !== null,
  );

  const rows = Object.entries(source)
    .filter(([k]) => !SKIP_FIELDS.has(k))
    .filter(
      ([k]) =>
        !(isItemCreationLike && k === "title" && hasOtherIdentifyingFields),
    )
    .filter(([k]) => !(hasParentDisplayId && k === "parent_title"))
    // No-op filter: only relevant when we're rendering a real diff (updates)
    .filter(([k, v]) => usingBefore || !before || before[k] !== v)
    .filter(([, v]) => !isUuid(v))
    .map(([k, v]) => ({
      key: k,
      label: humanKey(k),
      // Deletes render as single-column (no strikethrough before/after diff)
      before:
        !usingBefore && before && !isUuid(before[k])
          ? renderFieldValue(k, before[k])
          : null,
      after: renderFieldValue(k, v),
    }));

  if (isItemCreationLike) {
    const rank = (key: string) => {
      const i = ITEM_FIELD_ORDER.indexOf(key);
      return i === -1 ? ITEM_FIELD_ORDER.length : i;
    };
    rows.sort((a, b) => rank(a.key) - rank(b.key));
  }

  return rows;
}

// Fields whose values are long-form prose and benefit from an expand/collapse
// when their diff would otherwise take too much space.
const LONG_FIELDS = new Set(["description"]);
const LONG_VALUE_THRESHOLD = 80;

function isLongValue(v: string | null): boolean {
  return v !== null && v.length > LONG_VALUE_THRESHOLD;
}

function isLongRow(row: DiffRow): boolean {
  return (
    LONG_FIELDS.has(row.key) ||
    isLongValue(row.after) ||
    isLongValue(row.before)
  );
}

const DiffRowView: Component<{ row: DiffRow }> = (props) => {
  const [expanded, setExpanded] = createSignal(false);
  const isUpdate = () => props.row.before !== null;
  const long = () => isLongRow(props.row);

  return (
    <Show
      when={isUpdate()}
      fallback={
        <div class="text-xs text-ink-2">
          <span class="text-ink-3">{props.row.label}: </span>
          <span class="font-medium whitespace-pre-wrap">{props.row.after}</span>
        </div>
      }
    >
      <Show
        when={long()}
        fallback={
          <div class="text-xs text-ink-2">
            <span class="text-ink-3">{props.row.label}: </span>
            <span class="line-through text-ink-3">{props.row.before}</span>
            <span class="mx-1 text-ink-3">→</span>
            <span class="font-medium">{props.row.after}</span>
          </div>
        }
      >
        <div class="text-xs text-ink-2">
          <div class="flex items-center gap-2">
            <span class="text-ink-3">{props.row.label}: </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded());
              }}
              class="text-ink-2 hover:text-ink underline underline-offset-2"
            >
              <Show when={expanded()} fallback={<>Update to… (show)</>}>
                Hide
              </Show>
            </button>
          </div>
          <Show when={expanded()}>
            <ProposalDiff
              class="mt-1"
              before={props.row.before}
              after={props.row.after}
            />
          </Show>
        </div>
      </Show>
    </Show>
  );
};

type OperationType = "create" | "update" | "delete" | "status" | "other";

function getOperationType(toolName: string): OperationType {
  if (toolName === "create_work_item" || toolName === "create_child_item")
    return "create";
  if (toolName === "delete_work_item") return "delete";
  if (toolName === "change_work_item_status") return "status";
  if (
    toolName === "update_work_item" ||
    toolName === "assign_work_item" ||
    toolName === "set_priority" ||
    toolName === "set_parent" ||
    toolName === "remove_parent" ||
    toolName.startsWith("update_") ||
    toolName.startsWith("archive_") ||
    toolName.startsWith("publish_")
  )
    return "update";
  return "other";
}

const opStyles: Record<
  OperationType,
  { border: string; icon: string; iconClass: string }
> = {
  create: {
    border: "border-signal-green/25 bg-signal-green-tint",
    icon: "M12 4v16m8-8H4",
    iconClass: "text-signal-green",
  },
  update: {
    border: "border-line bg-surface",
    icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
    iconClass: "text-signal-blue",
  },
  delete: {
    border: "border-signal-red/25 bg-signal-red-tint",
    icon: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
    iconClass: "text-signal-red",
  },
  status: {
    border: "border-line bg-surface",
    icon: "M13 7l5 5m0 0l-5 5m5-5H6",
    iconClass: "text-signal-amber",
  },
  other: {
    border: "border-line bg-surface",
    icon: "M4 6h16M4 12h16M4 18h16",
    iconClass: "text-ink-3",
  },
};

function formatToolLabel(toolName: string): string {
  return toolName
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

// =============================================================================
// Outcome icon (for non-pending actions)
// =============================================================================

const OutcomeIcon: Component<{ status: ProposedAction["status"] }> = (
  props,
) => (
  <Show
    when={props.status === "executed"}
    fallback={
      <Show
        when={props.status === "failed"}
        fallback={
          <Show when={props.status === "approved"}>
            <Spinner size="sm" class="text-signal-blue" />
          </Show>
        }
      >
        <svg
          class="w-4 h-4 text-signal-red shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </Show>
    }
  >
    <svg
      class="w-4 h-4 text-signal-green shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
  </Show>
);

export const ActionDiffCard: Component<ActionDiffCardProps> = (props) => {
  const opType = createMemo(() => getOperationType(props.action.tool_name));
  const style = createMemo(() => opStyles[opType()]);
  const diffRows = createMemo(() => buildDiffRows(props.action));
  const hasError = createMemo(
    () =>
      props.action.status === "failed" &&
      !!props.action.execution_result?.error,
  );

  const handleClick = () => {
    if (props.selectable) props.onToggle();
  };

  return (
    <div
      class={
        "rounded border p-3 transition-colors " +
        style().border +
        (props.selectable ? " cursor-pointer hover:border-line-strong" : "") +
        (props.selected && props.selectable ? " !border-ink !bg-sunken" : "")
      }
      onClick={handleClick}
      role={props.selectable ? "checkbox" : undefined}
      aria-checked={props.selectable ? props.selected : undefined}
      tabIndex={props.selectable ? 0 : undefined}
      onKeyDown={(e) => {
        if (props.selectable && (e.key === " " || e.key === "Enter")) {
          e.preventDefault();
          props.onToggle();
        }
      }}
    >
      <div class="flex items-start gap-2.5">
        {/* Checkbox or outcome icon */}
        <div class="shrink-0 pt-0.5">
          <Show
            when={props.selectable}
            fallback={<OutcomeIcon status={props.action.status} />}
          >
            <input
              type="checkbox"
              checked={props.selected}
              onChange={props.onToggle}
              class="w-4 h-4 accent-ink border-line rounded focus-ring"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select action: ${formatToolLabel(props.action.tool_name)}`}
            />
          </Show>
        </div>

        {/* Operation icon */}
        <div class="shrink-0 pt-0.5">
          <svg
            class={"w-4 h-4 " + style().iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d={style().icon}
            />
          </svg>
        </div>

        {/* Content */}
        <div class="flex-1 min-w-0">
          <Show when={opType() !== "create" && opType() !== "delete"}>
            <div class="text-sm font-medium text-ink">
              {formatToolLabel(props.action.tool_name)}
            </div>
          </Show>

          {/* Empty-state fallback when we have no detail rows to show
              (e.g. a delete proposal whose before_snapshot never populated) */}
          <Show when={diffRows().length === 0}>
            <div class="text-xs italic text-ink-3">
              <Show
                when={opType() === "delete"}
                fallback="No additional details."
              >
                Item details unavailable.
              </Show>
            </div>
          </Show>

          {/* Diff rows */}
          <Show when={diffRows().length > 0}>
            <div
              class={props.compact ? "mt-1 space-y-0.5" : "mt-1.5 space-y-1"}
            >
              <For each={diffRows()}>{(row) => <DiffRowView row={row} />}</For>
            </div>
          </Show>

          {/* Error message */}
          <Show when={hasError()}>
            <div class="mt-2 text-xs text-signal-red bg-signal-red-tint border border-signal-red/25 rounded p-2">
              <span class="font-medium">Error: </span>
              {props.action.execution_result?.error}
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};

export default ActionDiffCard;
